/**
 * src/pages/DesignGallery.jsx — REDESIGN.md Phase 3 component gallery.
 *
 * Public, unauthenticated, reachable only by direct URL — same pattern as
 * DesignSpecimen.jsx (Phase 1): no NAV entry, no link from anywhere
 * production-facing, noindex at runtime. Demonstrates every component in
 * components/ui/ in every state, switchable across all 5 themes and both
 * modes (design-tokens/themes.js) — only "Tattoo Labs" is actually live
 * anywhere else in the app today (see theme-migration.md), but all five are
 * real, complete token sets, and this is exactly the review surface
 * REDESIGN.md intended them for.
 *
 * Theme/mode are read from the URL (`?theme=&mode=`), not just local
 * component state, specifically so tests/design/gallery-a11y.spec.js can
 * navigate directly to every one of the 10 theme x mode combinations for a
 * real axe pass on each — REDESIGN.md's Phase 3 "Done when" requires zero
 * violations in every combination, not just the default. The picker buttons
 * below update the same URL params, so interactive and automated review go
 * through the identical code path.
 */
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useNoindex } from '../lib/useNoindex.js';
import { THEME_IDS, THEME_LABELS, MODES } from '../design-tokens/themes.js';
import {
  Button,
  Link,
  Input,
  Textarea,
  Select,
  Checkbox,
  RadioGroup,
  DateField,
  TimeField,
  FileUpload,
  Dialog,
  Drawer,
  useToast,
  Tabs,
  Table,
  Th,
  RowHeader,
  StatusBadge,
  Avatar,
  EmptyState,
  Skeleton,
  Pagination,
  DashboardNav,
  PortalHeader,
} from '../components/ui/index.js';

const STATUSES = [
  'confirmed', 'signed', 'complete', 'completed', 'available',
  'pending', 'active', 'claimed',
  'cancelled', 'expired', 'no_show',
  'sold', 'paused',
];

export default function DesignGallery() {
  useNoindex();
  const [params, setParams] = useSearchParams();
  const theme = THEME_IDS.includes(params.get('theme')) ? params.get('theme') : 'tattoo-labs';
  const mode = MODES.includes(params.get('mode')) ? params.get('mode') : 'light';

  const setTheme = (next) => setParams((p) => (p.set('theme', next), new URLSearchParams(p)));
  const setMode = (next) => setParams((p) => (p.set('mode', next), new URLSearchParams(p)));

  return (
    // data-mode is always explicit (never absent) — this page's whole point
    // is deterministic, reviewable/testable states, so it always renders
    // exactly the requested mode rather than falling through to whatever
    // the browser's OS preference happens to be (design-tokens/themes.js's
    // real app usage leaves data-mode absent for "system" — this page
    // deliberately doesn't offer that third state, since there's nothing to
    // visually review in "follows the OS").
    <div className="specimen" data-theme={theme} data-mode={mode}>
      <header className="specimen-header">
        <div>
          <p className="specimen-eyebrow">REDESIGN.md · Phase 3 component gallery — not a production page</p>
          <h1 className="specimen-title">Components</h1>
          <p className="specimen-lead">
            Every component in components/ui/, in every state. Switch theme and mode below — the
            URL updates too, so any specific combination can be linked or navigated to directly.
          </p>
        </div>
      </header>

      <section className="specimen-section">
        <h2 className="specimen-section-title">Theme &amp; mode</h2>
        <div className="specimen-row" role="group" aria-label="Theme">
          {THEME_IDS.map((id) => (
            <button
              key={id}
              type="button"
              className={`specimen-btn specimen-btn-secondary${theme === id ? ' specimen-btn-primary' : ''}`}
              aria-pressed={theme === id}
              onClick={() => setTheme(id)}
            >
              {THEME_LABELS[id]}
            </button>
          ))}
        </div>
        <div className="specimen-row" role="group" aria-label="Mode" style={{ marginTop: 8 }}>
          {MODES.map((m) => (
            <button
              key={m}
              type="button"
              className={`specimen-btn specimen-btn-secondary${mode === m ? ' specimen-btn-primary' : ''}`}
              aria-pressed={mode === m}
              onClick={() => setMode(m)}
            >
              {m === 'light' ? 'Light' : 'Dark'}
            </button>
          ))}
        </div>
      </section>

      <ButtonsSection />
      <LinksSection />
      <FormFieldsSection />
      <DialogDrawerSection />
      <ToastSection />
      <TabsSection />
      <TableSection />
      <StatusBadgeSection />
      <AvatarSection />
      <EmptyStateSection />
      <SkeletonSection />
      <PaginationSection />
      <NavigationSection />
    </div>
  );
}

function ButtonsSection() {
  return (
    <section className="specimen-section">
      <h2 className="specimen-section-title">Buttons</h2>
      <p className="specimen-section-note">
        Hover, focus (tab to one) and active states are live. <code>Button</code> wraps the same
        <code>.btn</code> classes every page already uses.
      </p>
      <div className="specimen-row">
        <Button variant="primary">Set up your studio</Button>
        <Button variant="secondary">Sign in</Button>
        <Button variant="danger">Delete client</Button>
        <Button variant="ghost">Cancel</Button>
        <Button variant="primary" size="sm">
          Small
        </Button>
        <Button variant="primary" disabled>
          Disabled
        </Button>
        <Button variant="primary" loading>
          Saving…
        </Button>
      </div>
    </section>
  );
}

function LinksSection() {
  return (
    <section className="specimen-section">
      <h2 className="specimen-section-title">Links</h2>
      <p className="specimen-section-note">
        Always the accent colour, always underlined — colour alone never carries "this is a link."
      </p>
      <p>
        <Link to="/dashboard">An internal link (react-router)</Link> · <Link to="mailto:studio@example.com">An external link</Link>
      </p>
    </section>
  );
}

function FormFieldsSection() {
  const [radioValue, setRadioValue] = useState('no');
  return (
    <section className="specimen-section">
      <h2 className="specimen-section-title">Form fields</h2>
      <p className="specimen-section-note">
        Every field validates after interaction, not on first keystroke (<code>:user-invalid</code>
        ) — blur the email field empty, or type an invalid address and tab away, to see it.
      </p>
      <div className="specimen-form-grid">
        <Input id="gallery-name" label="Your full name" defaultValue="Maya Okafor" autoComplete="name" />
        <Input
          id="gallery-email"
          label="Email"
          type="email"
          required
          hint="We'll only use this to confirm your booking."
          autoComplete="email"
        />
        <Textarea id="gallery-notes" label="Notes" placeholder="Allergies, skin type, anything worth remembering." />
        <Select id="gallery-artist" label="Artist" required>
          <option value="">Choose an artist…</option>
          <option value="maya">Maya Okafor</option>
          <option value="tom">Tom Reilly</option>
        </Select>
        <DateField id="gallery-date" label="Preferred date" />
        <TimeField id="gallery-time" label="Preferred time" />
      </div>
      <div style={{ marginTop: 16 }}>
        <Checkbox id="gallery-consent" label="I confirm the details above are correct." />
      </div>
      <div style={{ marginTop: 16 }}>
        <RadioGroup
          legend="Do you have any known allergies to latex or adhesives?"
          name="gallery-allergy"
          value={radioValue}
          onChange={setRadioValue}
          options={[
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
          ]}
        />
      </div>
      <div style={{ marginTop: 16 }}>
        <FileUpload label="Reference images" hint="PNG or JPG, up to 10MB each." onFiles={() => {}} />
      </div>
    </section>
  );
}

function DialogDrawerSection() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  return (
    <section className="specimen-section">
      <h2 className="specimen-section-title">Dialog &amp; Drawer</h2>
      <p className="specimen-section-note">
        Real <code>&lt;dialog&gt;</code> elements — Escape, backdrop click, and Tab all trap/restore
        focus natively.
      </p>
      <div className="specimen-row">
        <Button variant="secondary" onClick={() => setDialogOpen(true)}>
          Open dialog
        </Button>
        <Button variant="secondary" onClick={() => setDrawerOpen(true)}>
          Open drawer
        </Button>
      </div>
      {dialogOpen ? (
        <Dialog title="Add client" onClose={() => setDialogOpen(false)}>
          <p>A centred dialog for a short, focused task.</p>
        </Dialog>
      ) : null}
      {drawerOpen ? (
        <Drawer title="Appointment detail" onClose={() => setDrawerOpen(false)}>
          <p>A side panel for a task that wants more room without leaving the page context.</p>
        </Drawer>
      ) : null}
    </section>
  );
}

function ToastSection() {
  const { push } = useToast();
  return (
    <section className="specimen-section">
      <h2 className="specimen-section-title">Toast</h2>
      <p className="specimen-section-note">
        The button says what will happen; the toast that follows echoes what did.
      </p>
      <div className="specimen-row">
        <Button variant="secondary" onClick={() => push('Deposit paid')}>
          Pay £50 deposit
        </Button>
        <Button variant="secondary" onClick={() => push('Consent form sent', { variant: 'success' })}>
          Send consent link
        </Button>
        <Button variant="secondary" onClick={() => push('Could not reach the server', { variant: 'error' })}>
          Trigger an error toast
        </Button>
      </div>
    </section>
  );
}

function TabsSection() {
  const [active, setActive] = useState('today');
  return (
    <section className="specimen-section">
      <h2 className="specimen-section-title">Tabs</h2>
      <p className="specimen-section-note">Arrow keys move focus between tabs; Enter/Space activates.</p>
      <Tabs
        label="Client history"
        active={active}
        onChange={setActive}
        tabs={[
          { id: 'today', label: 'Today', panel: <p>09:00 Maya Okafor — Fine line, session 2.</p> },
          { id: 'upcoming', label: 'Upcoming', panel: <p>No upcoming sessions booked yet.</p> },
          { id: 'past', label: 'Past', panel: <p>14 sessions completed since 2024.</p> },
        ]}
      />
    </section>
  );
}

function TableSection() {
  return (
    <section className="specimen-section">
      <h2 className="specimen-section-title">Table</h2>
      <Table caption="Today's sessions, 21 September">
        <thead>
          <tr>
            <Th>Time</Th>
            <Th>Client</Th>
            <Th>Session</Th>
            <Th>Status</Th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <RowHeader>09:00</RowHeader>
            <td>Maya Okafor</td>
            <td>Fine line, session 2</td>
            <td><StatusBadge status="signed" /></td>
          </tr>
          <tr>
            <RowHeader>11:30</RowHeader>
            <td>Tom Reilly</td>
            <td>Blackwork consult</td>
            <td><StatusBadge status="pending" /></td>
          </tr>
        </tbody>
      </Table>
    </section>
  );
}

function StatusBadgeSection() {
  return (
    <section className="specimen-section">
      <h2 className="specimen-section-title">Status badge</h2>
      <p className="specimen-section-note">Never colour-only — every status carries a glyph and text too.</p>
      <div className="specimen-row">
        {STATUSES.map((s) => (
          <StatusBadge key={s} status={s} />
        ))}
      </div>
    </section>
  );
}

function AvatarSection() {
  return (
    <section className="specimen-section">
      <h2 className="specimen-section-title">Avatar</h2>
      <div className="specimen-row">
        <Avatar name="Maya Okafor" size="sm" />
        <Avatar name="Tom Reilly" />
        <Avatar name="Priya Chandra" />
      </div>
    </section>
  );
}

function EmptyStateSection() {
  return (
    <section className="specimen-section">
      <h2 className="specimen-section-title">Empty state</h2>
      <div className="card">
        <EmptyState
          title="No clients yet"
          description="Add your first client to start booking sessions."
          action={<Button variant="primary">Add client</Button>}
        />
      </div>
    </section>
  );
}

function SkeletonSection() {
  return (
    <section className="specimen-section">
      <h2 className="specimen-section-title">Skeleton</h2>
      <div className="specimen-row" style={{ alignItems: 'center' }}>
        <Skeleton width="36px" height="36px" radius={false} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Skeleton width="40%" />
          <Skeleton width="70%" />
        </div>
      </div>
    </section>
  );
}

function PaginationSection() {
  const [page, setPage] = useState(2);
  return (
    <section className="specimen-section">
      <h2 className="specimen-section-title">Pagination</h2>
      <Pagination page={page} pageCount={5} onChange={setPage} />
    </section>
  );
}

function NavigationSection() {
  return (
    <section className="specimen-section">
      <h2 className="specimen-section-title">Navigation</h2>
      <p className="specimen-section-note">
        Dashboard's sidebar (real component, App.jsx's Shell renders this exact one) and the
        portal's header/step-progress pattern (built this phase, not wired into a real portal page
        until Phase 4). Marketing has no React nav to demo — it's static HTML, redesigned directly
        in Phase 6.
      </p>
      <div className="specimen-row" style={{ alignItems: 'flex-start' }}>
        <div style={{ width: 216, border: '1px solid var(--border)' }}>
          <DashboardNav isOwner={false} />
        </div>
        <div style={{ flex: 1 }} className="card">
          <PortalHeader studioName="Maya's Studio" step={{ current: 2, total: 4, label: 'Choose a time' }} />
        </div>
      </div>
    </section>
  );
}
